/**
 * AutoStore View - Main container for Shopify publishing pipeline
 *
 * Tabs:
 * - Queue: Products pending generation/approval/publishing
 * - Published: Products live on Shopify
 * - Stores: Configure Shopify store connections
 */

import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { useDropGenStore } from '../../../stores/dropgenStore';
import {
  getAutoStoreQueue,
  getAutoStorePublished,
  getAutoStoreStores,
  getAutoStoreStats,
  generateAutoStoreContent,
  generateAutoStoreContentBulk,
  approveAutoStoreItem,
  approveAutoStoreItemsBulk,
  rejectAutoStoreItem,
  publishAutoStoreItem,
  publishAutoStoreItemsBulk,
  unpublishAutoStoreItem,
  deleteAutoStoreQueueItem,
  updateAutoStoreQueueItem,
  createAutoStoreStore,
  updateAutoStoreStore,
  deleteAutoStoreStore,
  testAutoStoreConnection,
  checkAutoStoreHealth,
  getImageProcessorStatus,
  generateImageSuite,
  generateSingleImage,
  type QueueItem,
  type QueueStatus,
  type AutoStoreStore,
  type StoreInput,
  type ImageSuite,
} from '../api';
import { ProvisionModal } from './ProvisionModal';
import { ProvisionProgress } from './ProvisionProgress';

type TabType = 'queue' | 'published' | 'removed' | 'stores';

const STATUS_COLORS: Record<QueueStatus, string> = {
  pending: 'bg-gray-500',
  generating: 'bg-yellow-500 animate-pulse',
  ready: 'bg-blue-500',
  approved: 'bg-green-500',
  publishing: 'bg-purple-500 animate-pulse',
  published: 'bg-emerald-600',
  failed: 'bg-red-500',
  rejected: 'bg-gray-600',
};

const STATUS_LABELS: Record<QueueStatus, string> = {
  pending: 'Pending',
  generating: 'Generating...',
  ready: 'Ready',
  approved: 'Approved',
  publishing: 'Publishing...',
  published: 'Published',
  failed: 'Failed',
  rejected: 'Rejected',
};

export function AutoStoreView() {
  const { wsConnected } = useDropGenStore();
  const [activeTab, setActiveTab] = useState<TabType>('queue');
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [publishedItems, setPublishedItems] = useState<QueueItem[]>([]);
  const [removedItems, setRemovedItems] = useState<QueueItem[]>([]);
  const [stores, setStores] = useState<AutoStoreStore[]>([]);
  const [stats, setStats] = useState<Record<QueueStatus, number>>({} as Record<QueueStatus, number>);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('pending,ready,approved,failed');
  const [showAddStore, setShowAddStore] = useState(false);
  const [editingItem, setEditingItem] = useState<QueueItem | null>(null);
  const [healthStatus, setHealthStatus] = useState<{ ollama: string; database: string } | null>(null);
  const [provisioningStore, setProvisioningStore] = useState<AutoStoreStore | null>(null);
  const [activeProvisionJob, setActiveProvisionJob] = useState<{ id: string; storeName: string } | null>(null);
  const [imageProcessorAvailable, setImageProcessorAvailable] = useState<boolean>(false);
  const [generatingImagesFor, setGeneratingImagesFor] = useState<string | null>(null);

  // Check image processor status on mount
  useEffect(() => {
    const checkImageProcessor = async () => {
      try {
        const status = await getImageProcessorStatus();
        setImageProcessorAvailable(status.available && status.status !== 'offline');
      } catch {
        setImageProcessorAvailable(false);
      }
    };
    checkImageProcessor();
    // Recheck every 30 seconds
    const interval = setInterval(checkImageProcessor, 30000);
    return () => clearInterval(interval);
  }, []);

  // Handle image suite generation
  const handleGenerateImages = async (id: string) => {
    try {
      setGeneratingImagesFor(id);
      const result = await generateImageSuite(id);
      if (!result.success) {
        setError(result.error || 'Image generation failed');
      }
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Image generation failed');
    } finally {
      setGeneratingImagesFor(null);
    }
  };

  // Fetch data based on active tab
  const fetchData = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    setError(null);

    try {
      // Always fetch health status AND stores list on initial load for status indicators
      if (initialLoad) {
        const [healthRes, storesRes] = await Promise.all([
          checkAutoStoreHealth(),
          getAutoStoreStores(),
        ]);
        setHealthStatus({ ollama: healthRes.services.ollama, database: healthRes.services.database });
        setStores(storesRes.stores);
      }

      if (activeTab === 'queue') {
        const [queueRes, statsRes] = await Promise.all([
          getAutoStoreQueue({ status: statusFilter, limit: 100 }),
          getAutoStoreStats(),
        ]);
        setQueueItems(queueRes.items);
        setStats(statsRes.stats);
      } else if (activeTab === 'published') {
        const res = await getAutoStorePublished({ limit: 100 });
        setPublishedItems(res.items);
      } else if (activeTab === 'removed') {
        const res = await getAutoStoreQueue({ status: 'rejected', limit: 100 });
        setRemovedItems(res.items);
      } else if (activeTab === 'stores') {
        const [storesRes, healthRes] = await Promise.all([
          getAutoStoreStores(),
          checkAutoStoreHealth(),
        ]);
        setStores(storesRes.stores);
        setHealthStatus({ ollama: healthRes.services.ollama, database: healthRes.services.database });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [activeTab, statusFilter, initialLoad]);

  useEffect(() => {
    fetchData(initialLoad); // Only show loader on first load
  }, [fetchData]);

  // Selection handling
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (activeTab === 'queue') {
      setSelectedIds(new Set(queueItems.map(i => i.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Queue actions
  const handleGenerate = async (id: string) => {
    try {
      setLoading(true);
      await generateAutoStoreContent(id);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generate failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBulk = async () => {
    if (selectedIds.size === 0) return;
    try {
      setLoading(true);
      await generateAutoStoreContentBulk(Array.from(selectedIds));
      clearSelection();
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk generate failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      setLoading(true);
      await approveAutoStoreItem(id);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBulk = async () => {
    if (selectedIds.size === 0) return;
    try {
      setLoading(true);
      await approveAutoStoreItemsBulk(Array.from(selectedIds));
      clearSelection();
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk approve failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (id: string) => {
    try {
      setLoading(true);
      await rejectAutoStoreItem(id);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reject failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (id: string) => {
    try {
      setLoading(true);
      const result = await publishAutoStoreItem(id);
      if (result.shopify_url) {
        window.open(result.shopify_url, '_blank');
      }
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishBulk = async () => {
    if (selectedIds.size === 0) return;
    try {
      setLoading(true);
      await publishAutoStoreItemsBulk(Array.from(selectedIds));
      clearSelection();
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk publish failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublish = async (id: string) => {
    if (!confirm('Remove this product from Shopify?')) return;
    try {
      setLoading(true);
      await unpublishAutoStoreItem(id);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unpublish failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this item from queue?')) return;
    try {
      setLoading(true);
      await deleteAutoStoreQueueItem(id);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = async (id: string, updates: {
    generated_title?: string;
    generated_description?: string;
    generated_price?: number;
    price_floor?: number;
    price_ceiling?: number;
    generated_tags?: string[];
    generated_seo_title?: string;
    generated_seo_description?: string;
    store_id?: string;
  }) => {
    try {
      setLoading(true);
      await updateAutoStoreQueueItem(id, updates);
      setEditingItem(null);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  // Store actions
  const handleAddStore = async (input: StoreInput) => {
    try {
      setLoading(true);
      await createAutoStoreStore(input);
      setShowAddStore(false);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add store');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStore = async (id: string) => {
    if (!confirm('Delete this store configuration?')) return;
    try {
      setLoading(true);
      await deleteAutoStoreStore(id);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete store');
    } finally {
      setLoading(false);
    }
  };

  const handleTestStore = async (id: string) => {
    try {
      const result = await testAutoStoreConnection(id);
      alert(`Connected to: ${result.shop.name}\nPlan: ${result.shop.plan}\nProducts: ${result.shop.products_count}`);
    } catch (err) {
      alert(`Connection failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#0a0a0b] text-white">
      {/* Tab Bar - Glowing Segments */}
      <div className="relative px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-center">
        <div className="inline-flex p-1 bg-white/[0.03] rounded-xl border border-white/[0.06] backdrop-blur-sm overflow-x-auto scrollbar-hide">
          {(['queue', 'published', 'removed', 'stores'] as TabType[]).map(tab => {
            const isActive = activeTab === tab;
            const tabConfig: Record<TabType, { icon: string; gradient: string }> = {
              queue: { icon: '📦', gradient: 'from-purple-500 to-pink-500 shadow-purple-500/25' },
              published: { icon: '✅', gradient: 'from-emerald-500 to-green-500 shadow-emerald-500/25' },
              removed: { icon: '🗑️', gradient: 'from-gray-500 to-zinc-500 shadow-gray-500/25' },
              stores: { icon: '🏪', gradient: 'from-blue-500 to-indigo-500 shadow-blue-500/25' },
            };
            return (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); clearSelection(); }}
                className={`relative px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-medium text-xs sm:text-sm capitalize transition-all duration-300 whitespace-nowrap flex items-center gap-1.5 sm:gap-2 ${
                  isActive
                    ? `bg-gradient-to-r ${tabConfig[tab].gradient} text-white shadow-lg`
                    : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]'
                }`}
              >
                <span className={`transition-transform duration-300 ${isActive ? 'scale-110' : ''}`}>{tabConfig[tab].icon}</span>
                <span className="hidden sm:inline">{tab}</span>
                {tab === 'queue' && stats.pending > 0 && (
                  <span className={`ml-0.5 sm:ml-1 px-1.5 py-0.5 text-[10px] sm:text-xs rounded-full ${
                    isActive ? 'bg-white/20 text-white' : 'bg-purple-500/20 text-purple-400'
                  }`}>
                    {stats.pending}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Refresh button - right side */}
        <div className="absolute right-3 sm:right-4">
          <button
            onClick={() => fetchData(true)}
            className="p-2 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] rounded-lg transition-all hover:scale-105 active:scale-95"
          >
            <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* NOTE: Old tabs removed, keeping rest of rendering */}
      <div className="flex-1 overflow-auto bg-white/[0.02]">
        {/* Filter bar for queue - scrollable on mobile */}
        {activeTab === 'queue' && (
          <div className="px-2 sm:px-4 py-2 sm:py-3 border-b border-white/5 flex items-center justify-between gap-2">
            <div className="flex gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide">
              {(['all', 'pending', 'ready', 'approved', 'failed'] as const).map(filter => (
                <button
                  key={filter}
                  onClick={() => setStatusFilter(filter === 'all' ? 'pending,ready,approved,failed' : filter)}
                  className={`px-2 sm:px-3 py-1 text-[10px] sm:text-xs rounded-full transition-all whitespace-nowrap ${
                    (filter === 'all' && statusFilter === 'pending,ready,approved,failed') ||
                    statusFilter === filter
                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                      : 'text-gray-500 hover:text-gray-300 bg-white/5'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
            <span className="text-[10px] sm:text-xs text-gray-500 shrink-0">{queueItems.length} items</span>
          </div>
        )}

        {/* Content based on tab - simplified */}
        <div className="p-2 sm:p-4">
          {activeTab === 'queue' && renderQueueContent()}
          {activeTab === 'published' && renderPublishedContent()}
          {activeTab === 'removed' && renderRemovedContent()}
          {activeTab === 'stores' && renderStoresContent()}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/20 text-red-400 text-sm flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400/50 hover:text-red-400">×</button>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500" />
        </div>
      )}

      {/* Modals */}
      {showAddStore && <AddStoreModal onClose={() => setShowAddStore(false)} onSubmit={handleAddStore} />}
      {editingItem && (
        <EditItemModal
          item={editingItem}
          stores={stores}
          onClose={() => setEditingItem(null)}
          onSave={handleEditItem}
        />
      )}
      {provisioningStore && !activeProvisionJob && (
        <ProvisionModal
          store={provisioningStore}
          onClose={() => setProvisioningStore(null)}
          onStart={(job) => {
            setActiveProvisionJob({ id: job.id, storeName: provisioningStore.name });
            setProvisioningStore(null);
          }}
        />
      )}
      {activeProvisionJob && (
        <ProvisionProgress
          jobId={activeProvisionJob.id}
          storeName={activeProvisionJob.storeName}
          onClose={() => setActiveProvisionJob(null)}
          onComplete={() => {
            setActiveProvisionJob(null);
            fetchData();
          }}
        />
      )}
    </div>
  );

  // Render functions for each tab
  function renderQueueContent() {
    return (
      <div>
        {/* Bulk actions - responsive layout */}
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 p-2 sm:p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
            <span className="text-xs sm:text-sm text-purple-400">{selectedIds.size} selected</span>
            <button onClick={handleGenerateBulk} className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded hover:bg-yellow-500/30 transition-colors">
              Generate
            </button>
            <button onClick={handleApproveBulk} className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs bg-green-500/20 text-green-400 border border-green-500/30 rounded hover:bg-green-500/30 transition-colors">
              Approve
            </button>
            <button onClick={handlePublishBulk} className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded hover:bg-purple-500/30 transition-colors">
              Publish
            </button>
            <button onClick={clearSelection} className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs text-gray-500 hover:text-gray-300">
              Clear
            </button>
            <button onClick={selectAll} className="ml-auto px-2 sm:px-3 py-1 text-[10px] sm:text-xs text-gray-500 hover:text-gray-300">
              Select All
            </button>
          </div>
        )}

        {queueItems.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <p>No items in queue</p>
            <p className="text-sm text-gray-600 mt-1">Add products from Research Lab</p>
          </div>
        ) : (
          <div className="space-y-3">
            {queueItems.map(item => (
              <QueueItemCard
                key={item.id}
                item={item}
                selected={selectedIds.has(item.id)}
                onSelect={() => toggleSelection(item.id)}
                onGenerate={() => handleGenerate(item.id)}
                onApprove={() => handleApprove(item.id)}
                onReject={() => handleReject(item.id)}
                onPublish={() => handlePublish(item.id)}
                onEdit={() => setEditingItem(item)}
                onDelete={() => handleDelete(item.id)}
                onGenerateImages={() => handleGenerateImages(item.id)}
                imageProcessorAvailable={imageProcessorAvailable}
                generatingImages={generatingImagesFor === item.id}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderPublishedContent() {
    return (
          <div>
            {publishedItems.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                No published products yet.
              </div>
            ) : (
              <div className="space-y-3">
                {publishedItems.map(item => (
                  <div key={item.id} className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                    <div className="flex items-start gap-4">
                      {item.original_images?.[0] && (
                        <img
                          src={item.original_images[0]}
                          alt=""
                          className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80"
                          onClick={() => window.open(item.source_data?.product_url || `https://cjdropshipping.com`, '_blank')}
                          title="Open on CJ"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs rounded ${STATUS_COLORS.published}`}>
                            {STATUS_LABELS.published}
                          </span>
                          <span className="text-xs text-zinc-500">{item.store?.name}</span>
                        </div>
                        <h3
                          className="font-medium mt-1 truncate cursor-pointer hover:text-blue-400"
                          onClick={() => window.open(item.source_data?.product_url || `https://cjdropshipping.com`, '_blank')}
                          title="Open on CJ"
                        >
                          {item.generated_title}
                        </h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-zinc-400">
                          <span>${item.generated_price?.toFixed(2)}</span>
                          <span>CJ: ${item.cj_cost.toFixed(2)}</span>
                          {item.source_data?.sku && (
                            <span className="font-mono text-xs bg-zinc-700 px-1.5 py-0.5 rounded">
                              {item.source_data.sku}
                            </span>
                          )}
                          {item.published_at && (
                            <span>{new Date(item.published_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={item.source_data?.product_url || 'https://cjdropshipping.com'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-500 rounded"
                          title="Open on CJ"
                        >
                          CJ
                        </a>
                        {item.shopify_admin_url && (
                          <a
                            href={item.shopify_admin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 text-sm bg-emerald-700 hover:bg-emerald-600 rounded"
                          >
                            Shopify
                          </a>
                        )}
                        <button
                          onClick={() => handleUnpublish(item.id)}
                          className="px-3 py-1.5 text-sm bg-red-600/30 hover:bg-red-600/50 text-red-300 rounded"
                        >
                          Unpublish
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
    );
  }

  function renderRemovedContent() {
    return (
      <div>
        {removedItems.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <p>No removed products</p>
          </div>
        ) : (
          <div className="space-y-3">
            {removedItems.map(item => (
              <div key={item.id} className="p-4 bg-white/[0.03] rounded-lg border border-white/10">
                <div className="flex items-start gap-4">
                  {item.original_images?.[0] && (
                    <img
                      src={item.original_images[0]}
                      alt=""
                          className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80"
                          onClick={() => window.open(item.source_data?.product_url || `https://cjdropshipping.com`, '_blank')}
                          title="Open on CJ"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 text-xs rounded bg-gray-600">
                            Removed
                          </span>
                          <span className="text-xs text-zinc-500">{item.store?.name}</span>
                        </div>
                        <h3
                          className="font-medium mt-1 truncate cursor-pointer hover:text-blue-400"
                          onClick={() => window.open(item.source_data?.product_url || `https://cjdropshipping.com`, '_blank')}
                          title="Open on CJ"
                        >
                          {item.generated_title || item.source_data?.name || `Product ${item.cj_pid}`}
                        </h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-zinc-400">
                          <span>${item.generated_price?.toFixed(2) || 'N/A'}</span>
                          <span>CJ: ${item.cj_cost.toFixed(2)}</span>
                          {item.source_data?.sku && (
                            <span className="font-mono text-xs bg-zinc-700 px-1.5 py-0.5 rounded">
                              {item.source_data.sku}
                            </span>
                          )}
                          <span className="text-zinc-500">{item.source_data?.category}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={item.source_data?.product_url || 'https://cjdropshipping.com'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-500 rounded"
                          title="Open on CJ"
                        >
                          CJ
                        </a>
                        <button
                          onClick={() => handleApprove(item.id)}
                          className="px-3 py-1.5 text-sm bg-green-600/30 hover:bg-green-600/50 text-green-300 rounded"
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="px-3 py-1.5 text-sm bg-red-600/30 hover:bg-red-600/50 text-red-300 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
    );
  }

  function renderStoresContent() {
    return (
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-base sm:text-lg font-medium">Shopify Stores</h2>
          <button
            onClick={() => setShowAddStore(true)}
            className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 rounded-lg font-medium text-xs sm:text-sm shadow-lg shadow-purple-500/20 w-fit"
          >
            + Add Store
          </button>
        </div>

        {stores.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <p>No stores configured</p>
            <p className="text-sm text-gray-600 mt-1">Add a Shopify store to start publishing</p>
          </div>
        ) : (
          <div className="space-y-3">
            {stores.map(store => (
              <div key={store.id} className="p-4 bg-white/[0.03] rounded-lg border border-white/10">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{store.name}</h3>
                          {store.is_default && (
                            <span className="px-2 py-0.5 text-xs bg-blue-600 rounded">Default</span>
                          )}
                          {!store.is_active && (
                            <span className="px-2 py-0.5 text-xs bg-red-600 rounded">Inactive</span>
                          )}
                        </div>
                        <p className="text-sm text-zinc-400 mt-1">{store.shopify_domain}</p>
                        {store.categories.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {store.categories.map(cat => (
                              <span key={cat} className="px-2 py-0.5 text-xs bg-zinc-700 rounded">
                                {cat}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setProvisioningStore(store)}
                          className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-500 rounded font-medium"
                        >
                          Provision
                        </button>
                        <button
                          onClick={() => handleTestStore(store.id)}
                          className="px-3 py-1.5 text-sm bg-zinc-700 hover:bg-zinc-600 rounded"
                        >
                          Test
                        </button>
                        <button
                          onClick={() => handleDeleteStore(store.id)}
                          className="px-3 py-1.5 text-sm bg-red-600/30 hover:bg-red-600/50 text-red-300 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                <div className="mt-3 pt-3 border-t border-white/5 text-xs text-gray-500">
                  Markup: {store.settings?.default_markup || 3}x | Min Margin: {store.settings?.min_margin || 30}%
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

// Queue Item Card Component
function QueueItemCard({
  item,
  selected,
  onSelect,
  onGenerate,
  onApprove,
  onReject,
  onPublish,
  onEdit,
  onDelete,
  onGenerateImages,
  imageProcessorAvailable,
  generatingImages,
}: {
  item: QueueItem;
  selected: boolean;
  onSelect: () => void;
  onGenerate: () => void;
  onApprove: () => void;
  onReject: () => void;
  onPublish: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onGenerateImages: () => void;
  imageProcessorAvailable: boolean;
  generatingImages: boolean;
}) {
  const canGenerate = item.status === 'pending' || item.status === 'failed';
  const canApprove = item.status === 'ready';
  const canPublish = item.status === 'approved';
  const hasGeneratedImages = !!(item.source_data as any)?.generated_suite;
  const canGenerateImages = imageProcessorAvailable && (item.status === 'ready' || item.status === 'approved' || item.status === 'published');

  return (
    <div className={`p-3 sm:p-4 bg-zinc-800 rounded-lg border ${selected ? 'border-blue-500' : 'border-zinc-700'}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={selected}
            onChange={onSelect}
            className="mt-1 w-4 h-4 rounded bg-zinc-700 border-zinc-600"
          />

          {/* Image */}
          {item.original_images?.[0] && (
            <img src={item.original_images[0]} alt="" className="w-12 sm:w-16 h-12 sm:h-16 object-cover rounded" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <span className={`px-2 py-0.5 text-[10px] sm:text-xs rounded ${STATUS_COLORS[item.status]}`}>
              {STATUS_LABELS[item.status]}
            </span>
            {item.store && <span className="text-[10px] sm:text-xs text-zinc-500">{item.store.name}</span>}
            {item.pricing_confidence && (
              <span className="text-[10px] sm:text-xs text-zinc-500">{item.pricing_confidence.toFixed(0)}% conf</span>
            )}
          </div>

          <h3 className="font-medium text-sm sm:text-base mt-1 truncate">
            {item.generated_title || item.source_data?.name || `Product ${item.cj_pid}`}
          </h3>

          <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-xs sm:text-sm text-zinc-400">
            {item.generated_price ? (
              <span className="text-green-400">${item.generated_price.toFixed(2)}</span>
            ) : (
              <span>No price</span>
            )}
            <span>CJ: ${item.cj_cost.toFixed(2)}</span>
            <span className="text-zinc-500 hidden sm:inline">{item.source_data?.category}</span>
          </div>

          {item.pricing_reasoning && (
            <p className="text-[10px] sm:text-xs text-zinc-500 mt-1 truncate hidden sm:block">{item.pricing_reasoning}</p>
          )}

          {item.error_message && (
            <p className="text-[10px] sm:text-xs text-red-400 mt-1 truncate">{item.error_message}</p>
          )}
        </div>

        {/* Actions - wrap on mobile */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-2 sm:mt-0">
          {canGenerate && (
            <button onClick={onGenerate} className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-yellow-600 hover:bg-yellow-500 rounded">
              Generate
            </button>
          )}
          {canApprove && (
            <>
              <button onClick={onApprove} className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-green-600 hover:bg-green-500 rounded">
                Approve
              </button>
              <button onClick={onReject} className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-zinc-600 hover:bg-zinc-500 rounded">
                Reject
              </button>
            </>
          )}
          {canPublish && (
            <button onClick={onPublish} className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-purple-600 hover:bg-purple-500 rounded">
              Publish
            </button>
          )}
          {canGenerateImages && (
            <button
              onClick={onGenerateImages}
              disabled={generatingImages}
              className={`px-3 py-1.5 text-sm rounded ${
                generatingImages
                  ? 'bg-cyan-600/50 animate-pulse cursor-wait'
                  : hasGeneratedImages
                  ? 'bg-cyan-700 hover:bg-cyan-600'
                  : 'bg-cyan-600 hover:bg-cyan-500'
              }`}
              title={hasGeneratedImages ? 'Regenerate AI images' : 'Generate AI images (lifestyle, social, hero)'}
            >
              {generatingImages ? (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </span>
              ) : hasGeneratedImages ? (
                '🖼️ Regen'
              ) : (
                '🖼️ AI Images'
              )}
            </button>
          )}
          <button onClick={onEdit} className="p-1.5 text-zinc-500 hover:text-blue-400" title="Edit product details">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button onClick={onDelete} className="p-1.5 text-zinc-500 hover:text-red-400" title="Delete from queue">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// Add Store Modal Component
function AddStoreModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (input: StoreInput) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [token, setToken] = useState('');
  const [categories, setCategories] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !domain || !token) {
      setError('All fields are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({
        name,
        shopify_domain: domain,
        shopify_access_token: token,
        categories: categories.split(',').map(c => c.trim()).filter(Boolean),
        is_default: isDefault,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add store');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-zinc-800 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-bold mb-4">Add Shopify Store</h2>

        {error && (
          <div className="p-3 mb-4 bg-red-900/50 border border-red-700 rounded text-red-200 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Store Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="My Electronics Store"
              className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Shopify Domain</label>
            <input
              type="text"
              value={domain}
              onChange={e => setDomain(e.target.value)}
              placeholder="my-store.myshopify.com"
              className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Admin API Access Token</label>
            <input
              type="password"
              value={token}
              onChange={e => setToken(e.target.value)}
              placeholder="shpat_xxxxxxxxxx"
              className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Create in Shopify Admin → Settings → Apps → Develop apps
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Categories (comma-separated)</label>
            <input
              type="text"
              value={categories}
              onChange={e => setCategories(e.target.value)}
              placeholder="Electronics, Gadgets, Tech"
              className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Products matching these categories will route to this store
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isDefault"
              checked={isDefault}
              onChange={e => setIsDefault(e.target.checked)}
              className="w-4 h-4 rounded bg-zinc-700 border-zinc-600"
            />
            <label htmlFor="isDefault" className="text-sm">Set as default store</label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded disabled:opacity-50"
            >
              {loading ? 'Testing...' : 'Add Store'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Item Modal Component
function EditItemModal({
  item,
  stores,
  onClose,
  onSave,
}: {
  item: QueueItem;
  stores: AutoStoreStore[];
  onClose: () => void;
  onSave: (id: string, updates: {
    generated_title?: string;
    generated_description?: string;
    generated_price?: number;
    price_floor?: number;
    price_ceiling?: number;
    generated_tags?: string[];
    generated_seo_title?: string;
    generated_seo_description?: string;
    store_id?: string;
  }) => Promise<void>;
}) {
  const [title, setTitle] = useState(item.generated_title || '');
  const [description, setDescription] = useState(item.generated_description || '');
  const [price, setPrice] = useState(item.generated_price?.toString() || '');
  const [priceFloor, setPriceFloor] = useState(item.price_floor?.toString() || '');
  const [priceCeiling, setPriceCeiling] = useState(item.price_ceiling?.toString() || '');
  const [tags, setTags] = useState((item.generated_tags || []).join(', '));
  const [seoTitle, setSeoTitle] = useState(item.generated_seo_title || '');
  const [seoDescription, setSeoDescription] = useState(item.generated_seo_description || '');
  const [storeId, setStoreId] = useState(item.store_id || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSave(item.id, {
        generated_title: title || undefined,
        generated_description: description || undefined,
        generated_price: price ? parseFloat(price) : undefined,
        price_floor: priceFloor ? parseFloat(priceFloor) : undefined,
        price_ceiling: priceCeiling ? parseFloat(priceCeiling) : undefined,
        generated_tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
        generated_seo_title: seoTitle || undefined,
        generated_seo_description: seoDescription || undefined,
        store_id: storeId || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-zinc-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Edit Product</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-xl">&times;</button>
        </div>

        {/* Product Preview */}
        <div className="flex items-start gap-4 mb-6 p-3 bg-zinc-700/50 rounded-lg">
          {item.original_images?.[0] && (
            <img src={item.original_images[0]} alt="" className="w-20 h-20 object-cover rounded" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-zinc-400 truncate">CJ PID: {item.cj_pid}</p>
            <p className="text-sm text-zinc-400">CJ Cost: ${item.cj_cost.toFixed(2)}</p>
            <p className="text-sm text-zinc-400">Category: {item.source_data?.category || 'N/A'}</p>
          </div>
        </div>

        {/* Generated Images Gallery */}
        {(item.source_data as any)?.generated_suite && (
          <div className="mb-6">
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
              <span className="text-cyan-400">🖼️</span> AI Generated Images
            </h3>
            <div className="grid grid-cols-5 gap-2">
              {Object.entries((item.source_data as any).generated_suite as Record<string, string>).map(([type, url]) => (
                url && (
                  <div key={type} className="relative group">
                    <img
                      src={url}
                      alt={type}
                      className="w-full aspect-square object-cover rounded border border-zinc-600 hover:border-cyan-500 cursor-pointer transition-colors"
                      onClick={() => window.open(url, '_blank')}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-xs text-center py-1 rounded-b opacity-0 group-hover:opacity-100 transition-opacity">
                      {type.replace(/_/g, ' ')}
                    </div>
                  </div>
                )
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-2">Click to open full size. Generated images auto-upload to Supabase storage.</p>
          </div>
        )}

        {error && (
          <div className="p-3 mb-4 bg-red-900/50 border border-red-700 rounded text-red-200 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Product title for Shopify"
              className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Product description (HTML supported)"
              rows={4}
              className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
            />
          </div>

          {/* Pricing Row */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="29.99"
                className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Floor Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={priceFloor}
                onChange={e => setPriceFloor(e.target.value)}
                placeholder="24.99"
                className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ceiling Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={priceCeiling}
                onChange={e => setPriceCeiling(e.target.value)}
                placeholder="39.99"
                className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
            <input
              type="text"
              value={tags}
              onChange={e => setTags(e.target.value)}
              placeholder="gadget, electronics, portable"
              className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
            />
          </div>

          {/* SEO Title */}
          <div>
            <label className="block text-sm font-medium mb-1">SEO Title</label>
            <input
              type="text"
              value={seoTitle}
              onChange={e => setSeoTitle(e.target.value)}
              placeholder="SEO optimized title"
              className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
            />
          </div>

          {/* SEO Description */}
          <div>
            <label className="block text-sm font-medium mb-1">SEO Description</label>
            <textarea
              value={seoDescription}
              onChange={e => setSeoDescription(e.target.value)}
              placeholder="Meta description for search engines"
              rows={2}
              className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
            />
          </div>

          {/* Store Selection */}
          <div>
            <label className="block text-sm font-medium mb-1">Assign to Store</label>
            <select
              value={storeId}
              onChange={e => setStoreId(e.target.value)}
              className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
            >
              <option value="">-- Select Store --</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>
                  {store.name} ({store.shopify_domain})
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AutoStoreView;
